import React, { useRef, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import moment, { Moment } from "moment";
import { Controller, useForm, useWatch } from "react-hook-form";
import { TouchableOpacity, View } from "react-native";
import { ActivityIndicator, RadioButton, Text } from "react-native-paper";

import { sortBy } from "lodash";
import { FlatList } from "react-native";
import {
  ActionsContainer,
  Button,
  CheckboxItem,
  ErrorHelperText,
  KeyboardAvoidingView,
  LinkButton,
  ScreenContainer,
  ScrollableContainer,
  useErrorScroll,
} from "../../components";
import ActivityImage from "../../components/ActivityImage";
import DatePickerInput from "../../components/DatePickerInput";
import MemberPickerInput from "../../components/MemberPickerInput";
import SectionPickerInput from "../../components/SectionPickerInput";
import TextInputArea from "../../components/TextInputArea";
import CustomTextInput from "../../components/TextInput";
import IneligibleModal from "../DeferredCompScreen/IneligibleModal";
import { ACTIVITY_DETAILS_FRAGMENT, CURRENT_MEMBER_FRAGMENT, CurrentMember } from "../fragments";
import { ActivityDetails, ScreenProps } from "../types";
import { ActivityUpdate, getActivityUpdates, isActivityUpdatedOrUnavailable, isEligible, planStartYear } from "../utils";
import { FullDescModal, SuccessModal } from "./modals";
import { FormData } from "./types";

export const MUTATION = gql`
  mutation createDeferredCompSubmission($input: DeferredCompCreateSubmissionInput!) {
    createDeferredCompSubmission(input: $input) {
      message
      success
      submission {
        id
        expirationDate
      }
    }
  }
`;

export const ACTIVITY_QUERY = gql`
  query getActivity($code: String!) {
    currentMember {
      ...CurrentMemberFragment
    }
    currentPlan: currentDeferredCompPlan {
      id
      startsOn
      endsOn
      activity: activityByCode(code: $code) {
        ...ActivityDetailsFragment
        tags {
          id: name
          name
        }
      }
    }
    planTransition: deferredCompPlanTransition {
      previousPlan {
        id
        startsOn
        endsOn
        activity: activityByCode(code: $code) {
          ...ActivityDetailsFragment
          tags {
            id: name
            name
          }
        }
      }
    }
  }
  ${ACTIVITY_DETAILS_FRAGMENT}
  ${CURRENT_MEMBER_FRAGMENT}
`;

interface ActivityPlan {
  id: string;
  startsOn: string;
  endsOn: string;
  activity?: ActivityDetails;
}

export interface ActivityQuery {
  currentPlan: ActivityPlan;
  planTransition?: {
    previousPlan: ActivityPlan;
  };
  currentMember: CurrentMember;
}

const defaultValues: FormData = {
  activityLevel: "",
  dateOfCompletion: "",
  memberToReview: undefined,
  activityDescription: "",
  checkboxTos: false,
  eventName: "",
  crmSectionId: "",
};

const DEFAULT_ERROR = "An unknown error occurred while submitting this form. Please try again.";

function getCurrentPlan(
  plans: ActivityPlan[],
  activityUpdates: ActivityUpdate[],
  dateOfCompletion: string,
  activityId?: string
) {
  if (activityId) {
    return plans.find((p) => p.activity?.id === activityId);
  }

  const [currentPlan, previousPlan] = plans;

  if (dateOfCompletion && activityUpdates.length === 0 && currentPlan?.activity && previousPlan?.activity) {
    const planWithinDate = plans.find((plan) =>
      moment(dateOfCompletion).isBetween(moment(plan.startsOn), moment(plan.endsOn), null, "[]")
    );
    if (planWithinDate) return planWithinDate;
  }

  return currentPlan;
}

export function validateCompletionDate(selectedDate: Moment) {
  const today = moment().startOf("day");
  const sixtyDaysAgo = moment(today).subtract(60, "days");

  if (selectedDate.isAfter(today)) {
    return "Future date selected. Activity completion date must have occurred in the last 60 days.";
  }

  if (selectedDate.isBefore(sixtyDaysAgo)) {
    return "Activity completion date must have occurred in the last 60 days.";
  }
}

export default function ActivityRequestPointsScreen({ navigation, route }: ScreenProps<"ActivityRequestPoints">) {
  const { data } = useQuery<ActivityQuery>(ACTIVITY_QUERY, { variables: { code: route.params.code } });
  const [sendMutation] = useMutation(MUTATION);
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<FormData>({ defaultValues, mode: "onChange" });

  const dateOfCompletion = useWatch({
    control,
    name: "dateOfCompletion",
  });

  const [apiError, setApiError] = useState("");
  const flatListRef = useRef<FlatList>(null);
  const { Divider, onSubmitError, scrollToField } = useErrorScroll({ defaultValues, flatListRef });
  const [fullDescModalVisible, setFullDescModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [expirationDate, setExpirationDate] = useState("");
  const [showIneligibleModal, setShowIneligibleModal] = useState(false);

  const plans = [data?.currentPlan, data?.planTransition?.previousPlan].filter((p) => !!p?.activity) as ActivityPlan[];
  const activityUpdates = getActivityUpdates(data?.currentPlan?.activity, data?.planTransition?.previousPlan?.activity);
  const plan = getCurrentPlan(plans, activityUpdates, dateOfCompletion, route.params.id);
  const activity = plan?.activity;
  const eligibilityStatus = data?.currentMember?.eligibilityStatus;
  const enrolled = data?.currentMember.enrolled || false;

  if (!activity) return <ActivityIndicator />;

  const { name, description, code } = activity;
  const participationLevels = sortBy(activity.participationLevels, [(l) => -l.points, (l) => l.requirements]);
  const shouldShowOptionalFields =
    code === "tournament_lead_host_facility_" || code === "volunteer_tournament_rules_official_referee_";

  const onSubmit = async (data: FormData) => {
    if (!isEligible(eligibilityStatus)) {
      return setShowIneligibleModal(true);
    }
    try {
      const {
        data: {
          createDeferredCompSubmission: { success, message, submission },
        },
      } = await sendMutation({
        variables: {
          input: {
            comments: data.activityDescription,
            participationLevelId: participationLevels[Number(data.activityLevel)]?.id,
            completionDate: data.dateOfCompletion,
            reviewerId: data.memberToReview?.id,
            eventName: shouldShowOptionalFields ? data.eventName : undefined,
            crmSectionId: shouldShowOptionalFields ? data.crmSectionId : undefined,
          },
        },
      });
      if (success) {
        setExpirationDate(submission.expirationDate);
        setSuccessModalVisible(true);
      } else {
        setApiError(message || DEFAULT_ERROR);
      }
    } catch (e: any) {
      const message = e && e.message ? e.message : DEFAULT_ERROR;
      setApiError(message);
    }
  };

  return (
    <ScreenContainer
      testID="ActivityRequestPointsScreen"
      navigationTitle="Request Points"
      goBack={() => {
        navigation.navigate("Activity", { code: activity.code });
      }}
    >
      <FullDescModal
        {...{
          visible: fullDescModalVisible,
          onDismiss: () => setFullDescModalVisible(false),
          name,
          description,
        }}
      />
      <SuccessModal
        {...{
          visible: successModalVisible,
          onClose: () => {
            setSuccessModalVisible(false);
            navigation.navigate("DeferredComp");
          },
          expirationDate,
        }}
      />
      <IneligibleModal
        {...{
          visible: showIneligibleModal,
          onDismiss: () => setShowIneligibleModal(false),
          eligibilityStatus,
          enrolled,
        }}
      />
      <KeyboardAvoidingView>
        <ScrollableContainer ref={flatListRef}>
          <View className="m-6 flex-row justify-between">
            <View style={{ width: 80, height: 80 }}>
              <ActivityImage
                style={{ flex: 1, width: undefined, height: undefined }}
                imageUrl={activity.imageUrl}
                imageClassName="rounded-lg bg-sky-light"
              />
            </View>
            <View className="flex-1 items-start ml-6">
              <View>
                {isActivityUpdatedOrUnavailable(activityUpdates) && (
                  <View className="bg-primary-light self-start rounded-full overflow-hidden px-2 py-1">
                    <Text className="text-tiny-tight font-500 text-sky-white">{planStartYear(plan)} Plan Year</Text>
                  </View>
                )}
                <Text className="mt-2 text-large-tight font-700 text-ink-darkest pb-2">{name}</Text>
                <Text numberOfLines={2} className="text-small-normal font-400 text-ink">
                  {description}
                </Text>
              </View>

              <LinkButton onPress={() => setFullDescModalVisible(true)}>Full Description</LinkButton>
            </View>
          </View>

          <Divider field="activityLevel" />

          <View className="m-6">
            <Text className="text-large-normal font-700 text-ink-darkest pb-4">Activity Level</Text>
            <Controller
              name="activityLevel"
              control={control}
              rules={{
                required: "Activity Level is required",
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  {participationLevels.map(({ id, points, requirements }, index) => {
                    const activityLevel = String(index);
                    return (
                      <TouchableOpacity
                        key={id}
                        onPress={() => onChange(activityLevel)}
                        activeOpacity={0.7}
                        className={`flex flex-row rounded-lg mb-3 ${
                          value === activityLevel ? "border-primary-lighter border-2 p-3" : "border-sky-dark border p-[13px]"
                        }`}
                      >
                        <View className="self-center mr-2">
                          <RadioButton.Android
                            value={id}
                            status={value === activityLevel ? "checked" : "unchecked"}
                            onPress={() => onChange(activityLevel)}
                            testID={`radio-button-${id}`}
                          />
                        </View>
                        <View className="flex-1">
                          <Text className="font-700 text-regular-tight text-ink-darkest mb-1">{points} points</Text>
                          <Text>{requirements}</Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                  {error && <ErrorHelperText error={error}>{error?.message}</ErrorHelperText>}
                </>
              )}
            />
          </View>

          <Divider field="dateOfCompletion" />

          <View className="m-6">
            <Text className="text-large-normal font-700 text-ink-darkest pb-4">Activity Completion Date</Text>
            <Text className="text-small-normal font-400 text-ink pb-4">
              Enter the date in which the eligible activity was completed.
            </Text>
            <Controller
              name="dateOfCompletion"
              control={control}
              rules={{
                required: "Date is required",
              }}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <DatePickerInput
                  title="Activity Completion Date"
                  testID="DatePickerInput"
                  description="Activity must have concluded within the last 60 days"
                  validate={validateCompletionDate}
                  {...{ onChange, onBlur, value, error }}
                  placeholder="Date of Completion"
                />
              )}
            />
          </View>

          <Divider field="memberToReview" />

          <View className="m-6">
            <Text className="text-large-normal font-700 text-ink-darkest pb-4">Member to Review</Text>
            <Text className="text-small-normal font-400 text-ink pb-4">
              Designate the authorized PGA Member who will review your submitted activity.
            </Text>

            <Controller
              name="memberToReview"
              control={control}
              rules={{
                required: "Member to review is required",
              }}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <MemberPickerInput
                  title="Member to Review"
                  placeholder="Member to Review"
                  testID="MemberPickerInput"
                  description="Select the Member who will review your activity."
                  {...{ onChange, onBlur, value, error }}
                />
              )}
            />
          </View>

          {shouldShowOptionalFields && (
            <>
              <Divider field="eventName" />
              <View className="m-6">
                <Text className="text-large-normal font-700 text-ink-darkest pb-4">Event</Text>
                <Text className="text-small-normal font-400 text-ink pb-4">
                  Enter the name of the tournament or event where the activity took place.
                </Text>
                <Controller
                  name="eventName"
                  control={control}
                  rules={{
                    required: "Event name is required",
                  }}
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <View>
                      <CustomTextInput
                        onChangeText={onChange}
                        onBlur={onBlur}
                        value={value}
                        placeholder="Event Name"
                        maxLength={100}
                        onFocus={() => {
                          scrollToField("eventName");
                        }}
                        error={error}
                      />
                    </View>
                  )}
                />
              </View>

              <Divider field="crmSectionId" />
              <View className="m-6">
                <Text className="text-large-normal font-700 text-ink-darkest pb-4">Location (PGA Section)</Text>
                <Text className="text-small-normal font-400 text-ink pb-4">
                  Select the PGA section where the activity took place.
                </Text>
                <Controller
                  name="crmSectionId"
                  control={control}
                  rules={{
                    required: "Section is required",
                  }}
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <SectionPickerInput
                      title="Section"
                      testID="SectionPickerInput"
                      description="Select the PGA section where the activity took place."
                      {...{ onChange, onBlur, error }}
                      value={value || ""}
                    />
                  )}
                />
              </View>
            </>
          )}

          <Divider field="activityDescription" />

          <View className="m-6">
            <Text className="text-large-normal font-700 text-ink-darkest pb-4">Activity Description</Text>
            <Text className="text-small-normal font-400 text-ink pb-4">
              Enter a brief description of your participation in the submitted activity (limit 500 characters).
            </Text>

            <Controller
              name="activityDescription"
              control={control}
              rules={{
                required: "Activity description is required",
                maxLength: { value: 500, message: "Maximum text length reached." },
              }}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <TextInputArea
                  error={error}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  value={value}
                  placeholder="Description"
                  onFocus={() => {
                    scrollToField("activityDescription");
                  }}
                />
              )}
            />
          </View>

          <Divider field="checkboxTos" />

          <View className="my-6 ml-5 mr-6 flex-1">
            <Controller
              name="checkboxTos"
              control={control}
              rules={{
                required: "Agreement is required",
              }}
              render={({ field: { ref, ...fieldProps }, fieldState: { error } }) => (
                <CheckboxItem
                  {...fieldProps}
                  label="I certify and affirm that the information I have provided in connection with this request for PGA Deferred Compensation Plan points is complete and accurate. I further acknowledge my understanding that (i) PGA may require additional evidence demonstrating my completion of the applicable activity, and (ii) my submission of incomplete and/or inaccurate information may result in my conduct being reviewed under the PGA's Code of Ethics."
                  labelStyle="text-regular-normal text-left mx-2"
                  className="items-start px-0 mx-0"
                  testID="checkboxTos"
                  error={error}
                />
              )}
            />
          </View>
          <ActionsContainer errorAlert errorMessage={apiError} onCloseErrorMessage={setApiError}>
            <Button
              loading={isSubmitting}
              disabled={isSubmitting}
              isValid={isValid}
              onPress={handleSubmit(onSubmit, onSubmitError)}
              mode="contained"
              icon="send"
              iconPosition="right"
            >
              Send for Review
            </Button>
          </ActionsContainer>
        </ScrollableContainer>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}
